<script setup lang="ts">
import DOMPurify from 'dompurify'
import { computed, nextTick, onMounted, ref, useSlots, watch } from 'vue'

type HtmlContent = {
  before?: string | null
  after?: string | null
}

const {
  tag = 'div',
  html = '',
  preserveBreaks = false,
  alignLeft = false,
} = defineProps<{
  tag?: string
  html?: string | HtmlContent
  preserveBreaks?: boolean
  alignLeft?: boolean
}>()

const slots = useSlots()
const element = ref<HTMLElement | null>(null)

const htmlContent = computed(() => {
  const { before = null, after = null } = typeof html === 'string' ? { after: html } : html

  return {
    afterbegin: before,
    beforeend: after,
  } satisfies Partial<Record<InsertPosition, string | null>>
})

const processLinks = () => {
  if (!element.value) return

  element.value.querySelectorAll<HTMLAnchorElement>('a[href^="http"]').forEach((externalLink) => {
    externalLink.target = '_blank'
    externalLink.rel = 'noopener'
  })
}

const renderHtml = async () => {
  await nextTick()

  if (!element.value) return

  const slotNodes = (slots.default?.() ?? [])
    .map((vnode) => vnode.el)
    .filter((node): node is Node => node instanceof Node)
    .map((node) => node.cloneNode(true))

  element.value.innerHTML = ''
  slotNodes.forEach((node) => element.value?.appendChild(node))

  Object.entries(htmlContent.value).forEach(([position, content]) => {
    if (!content) return

    element.value?.insertAdjacentHTML(position as InsertPosition, DOMPurify.sanitize(content))
  })

  processLinks()
}

onMounted(renderHtml)
watch(htmlContent, renderHtml, { deep: true })
</script>

<template>
  <component
    :is="tag"
    ref="element"
    :class="['raw-html', { '-pre-line': preserveBreaks, '-align-left': alignLeft }]"
  >
    <slot />
  </component>
</template>
